import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, get } from "firebase/database";
import {
  updateDefaultDailyLimit,
  setCustomDailyLimit,
  removeCustomDailyLimit,
} from "./utils/dailyUsageManager";
import { getCustomLimitUsers } from "./utils/usageTracking";
import { USAGE_CONFIG } from "./config/usageConfig";
import "./AdminUserManagement.css"; // Reuse existing CSS

const AdminUsageManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [defaultLimit, setDefaultLimit] = useState(USAGE_CONFIG.DEFAULT_DAILY_LIMIT);
  const [newDefaultLimit, setNewDefaultLimit] = useState(USAGE_CONFIG.DEFAULT_DAILY_LIMIT);
  const [customLimitUsers, setCustomLimitUsers] = useState([]);

  // State cho tùy chỉnh user
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customLimit, setCustomLimit] = useState(USAGE_CONFIG.DEFAULT_DAILY_LIMIT);
  const [reason, setReason] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // Load default limit và custom limit users
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load default limit
        const db = getDatabase();
        const defaultLimitRef = ref(db, `/system-settings/dailyLimits/defaultLimit`);
        const snapshot = await get(defaultLimitRef);

        if (snapshot.exists()) {
          setDefaultLimit(snapshot.val());
          setNewDefaultLimit(snapshot.val());
        }

        // Load custom limit users
        const customUsers = await getCustomLimitUsers();
        setCustomLimitUsers(customUsers);

        setLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        showToast("Lỗi khi tải dữ liệu", "error");
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Update default limit
  const handleUpdateDefaultLimit = async () => {
    try {
      if (newDefaultLimit < 1 || newDefaultLimit > 100) {
        showToast("Giới hạn phải từ 1 đến 100", "error");
        return;
      }

      const adminUserId = localStorage.getItem("userId");
      await updateDefaultDailyLimit(newDefaultLimit, adminUserId);

      setDefaultLimit(newDefaultLimit);
      showToast(`Đã cập nhật giới hạn mặc định: ${newDefaultLimit} lượt/ngày`);
    } catch (error) {
      console.error("Error updating default limit:", error);
      showToast("Lỗi khi cập nhật giới hạn", "error");
    }
  };

  // Search users
  const handleSearchUsers = async () => {
    if (!searchEmail.trim()) {
      showToast("Vui lòng nhập email", "error");
      return;
    }

    try {
      const db = getDatabase();
      const usersRef = ref(db, `/users`);
      const snapshot = await get(usersRef);

      if (!snapshot.exists()) {
        setSearchResults([]);
        showToast("Không tìm thấy user", "error");
        return;
      }

      const usersData = snapshot.val();
      const results = [];

      Object.keys(usersData).forEach((userId) => {
        const userData = usersData[userId];
        const email = userData.profile?.email || userData.email || "";

        if (email.toLowerCase().includes(searchEmail.toLowerCase())) {
          results.push({
            userId,
            email,
            displayName: userData.profile?.displayName || "Unknown",
          });
        }
      });

      setSearchResults(results);

      if (results.length === 0) {
        showToast("Không tìm thấy user", "error");
      }
    } catch (error) {
      console.error("Error searching users:", error);
      showToast("Lỗi khi tìm kiếm", "error");
    }
  };

  // Set custom limit
  const handleSetCustomLimit = async () => {
    if (!selectedUser) {
      showToast("Vui lòng chọn user", "error");
      return;
    }

    if (customLimit < 1 || customLimit > 1000) {
      showToast("Giới hạn phải từ 1 đến 1000", "error");
      return;
    }

    if (!reason.trim()) {
      showToast("Vui lòng nhập lý do", "error");
      return;
    }

    try {
      const adminUserId = localStorage.getItem("userId");
      let expiresAt = null;

      if (expiresInDays && parseInt(expiresInDays) > 0) {
        expiresAt = Date.now() + parseInt(expiresInDays) * 24 * 60 * 60 * 1000;
      }

      await setCustomDailyLimit(
        selectedUser.userId,
        customLimit,
        reason,
        adminUserId,
        expiresAt
      );

      showToast(
        `Đã set giới hạn ${customLimit} lượt/ngày cho ${selectedUser.email}`
      );

      // Reload custom limit users
      const customUsers = await getCustomLimitUsers();
      setCustomLimitUsers(customUsers);

      // Reset form
      setSelectedUser(null);
      setSearchEmail("");
      setSearchResults([]);
      setCustomLimit(3);
      setReason("");
      setExpiresInDays("");
    } catch (error) {
      console.error("Error setting custom limit:", error);
      showToast("Lỗi khi set giới hạn", "error");
    }
  };

  // Remove custom limit
  const handleRemoveCustomLimit = async (userId, email) => {
    if (!window.confirm(`Xóa giới hạn custom cho ${email}?`)) {
      return;
    }

    try {
      await removeCustomDailyLimit(userId);
      showToast(`Đã xóa giới hạn custom cho ${email}`);

      // Reload custom limit users
      const customUsers = await getCustomLimitUsers();
      setCustomLimitUsers(customUsers);
    } catch (error) {
      console.error("Error removing custom limit:", error);
      showToast("Lỗi khi xóa giới hạn", "error");
    }
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              <h3>Đang tải dữ liệu...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content">
      {/* Toast */}
      {toast.show && (
        <div className={`toast ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-message">{toast.message}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="content-header">
        <div>
          <h2>Quản lý giới hạn sử dụng</h2>
          <p>Tùy chỉnh số lượt sử dụng cho toàn hệ thống hoặc từng user</p>
        </div>
        <button
          onClick={() => navigate("/admin-dashboard")}
          className="btn btn-primary"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Quay lại Dashboard
        </button>
      </div>

      {/* Section 1: Default Limit */}
      <div className="data-table" style={{ marginBottom: "24px" }}>
        <div className="table-header">
          <div className="table-title">
            <h3>Giới hạn mặc định toàn hệ thống</h3>
          </div>
        </div>
        <div className="table-content" style={{ padding: "24px" }}>
          <div style={{ marginBottom: "16px" }}>
            <p style={{ color: "#666", marginBottom: "8px" }}>
              Giới hạn hiện tại: <strong>{defaultLimit} lượt/ngày</strong>
            </p>
            <p style={{ color: "#888", fontSize: "14px" }}>
              Áp dụng cho tất cả users không có giới hạn riêng
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <input
              type="number"
              min="1"
              max="100"
              value={newDefaultLimit}
              onChange={(e) => setNewDefaultLimit(parseInt(e.target.value))}
              className="form-input"
              style={{ width: "150px" }}
            />
            <span>lượt/ngày</span>
            <button
              onClick={handleUpdateDefaultLimit}
              className="btn btn-success"
              disabled={newDefaultLimit === defaultLimit}
            >
              Cập nhật
            </button>
          </div>
        </div>
      </div>

      {/* Section 2: Set Custom Limit */}
      <div className="data-table" style={{ marginBottom: "24px" }}>
        <div className="table-header">
          <div className="table-title">
            <h3>Tùy chỉnh giới hạn cho user cụ thể</h3>
          </div>
        </div>
        <div className="table-content" style={{ padding: "24px" }}>
          {/* Search user */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
              1. Tìm user (theo email)
            </label>
            <div style={{ display: "flex", gap: "12px" }}>
              <input
                type="text"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearchUsers()}
                placeholder="Nhập email user..."
                className="form-input"
                style={{ flex: 1 }}
              />
              <button onClick={handleSearchUsers} className="btn btn-primary">
                Tìm kiếm
              </button>
            </div>

            {/* Search results */}
            {searchResults.length > 0 && (
              <div style={{ marginTop: "12px", border: "1px solid #ddd", borderRadius: "8px" }}>
                {searchResults.map((user) => (
                  <div
                    key={user.userId}
                    onClick={() => setSelectedUser(user)}
                    style={{
                      padding: "12px",
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                      backgroundColor: selectedUser?.userId === user.userId ? "#e3f2fd" : "white",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#f5f5f5"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = selectedUser?.userId === user.userId ? "#e3f2fd" : "white"}
                  >
                    <div style={{ fontWeight: "500" }}>{user.displayName}</div>
                    <div style={{ fontSize: "14px", color: "#666" }}>{user.email}</div>
                  </div>
                ))}
              </div>
            )}

            {selectedUser && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px",
                  backgroundColor: "#e8f5e9",
                  borderRadius: "8px",
                }}
              >
                ✓ Đã chọn: <strong>{selectedUser.email}</strong>
              </div>
            )}
          </div>

          {/* Set custom limit */}
          {selectedUser && (
            <>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  2. Số lượt mới (lượt/ngày)
                </label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={customLimit}
                  onChange={(e) => setCustomLimit(parseInt(e.target.value))}
                  className="form-input"
                  style={{ width: "150px" }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  3. Lý do (bắt buộc)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="VD: VIP user, testing, support..."
                  className="form-input"
                />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  4. Thời hạn (tùy chọn - để trống = vĩnh viễn)
                </label>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <input
                    type="number"
                    min="1"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                    placeholder="Số ngày"
                    className="form-input"
                    style={{ width: "150px" }}
                  />
                  <span>ngày</span>
                </div>
              </div>

              <button onClick={handleSetCustomLimit} className="btn btn-success">
                Áp dụng giới hạn riêng
              </button>
            </>
          )}
        </div>
      </div>

      {/* Section 3: Custom Limit Users List */}
      <div className="data-table">
        <div className="table-header">
          <div className="table-title">
            <h3>Danh sách users có giới hạn riêng ({customLimitUsers.length})</h3>
          </div>
        </div>
        <div className="table-content">
          {customLimitUsers.length === 0 ? (
            <div className="empty-state" style={{ padding: "40px" }}>
              <p>Chưa có user nào có giới hạn riêng</p>
            </div>
          ) : (
            <table className="modern-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Giới hạn</th>
                  <th>Lý do</th>
                  <th>Hết hạn</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {customLimitUsers.map((user) => (
                  <tr key={user.userId}>
                    <td>
                      <div className="user-name">{user.displayName}</div>
                    </td>
                    <td className="user-email">{user.email}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor: "#e3f2fd",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontWeight: "500",
                        }}
                      >
                        {user.dailyLimit} lượt/ngày
                      </span>
                    </td>
                    <td>{user.reason}</td>
                    <td>
                      {user.expiresAt ? (
                        <span className={user.isExpired ? "text-error" : "text-success"}>
                          {new Date(user.expiresAt).toLocaleDateString("vi-VN")}
                          {user.isExpired && " (Đã hết hạn)"}
                        </span>
                      ) : (
                        <span className="text-muted">Vĩnh viễn</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => handleRemoveCustomLimit(user.userId, user.email)}
                        className="btn btn-danger"
                        style={{ padding: "6px 12px", fontSize: "14px" }}
                      >
                        Xóa
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsageManagement;
